import React, { useState } from 'react';
import axios from 'axios';
import {
    Container, Typography, Box, Grid,
    TextField, FormControl, InputLabel,
    Select, MenuItem, IconButton, Button,
    FormHelperText
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

function MonthlyReport() {
    const INITIAL_STATE = {
        intervals: [{lower: "09", upper: "12"}, {lower: "12", upper: "13"}, {lower: "13", upper: "18"}]
    }

    const currentYear = new Date().getFullYear();
    const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    const [icalUrl, setIcalUrl] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [userName, setUserName] = useState('');
    const [job, setJob] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const [intervals, setIntervals] = useState(INITIAL_STATE.intervals);
    const [activities, setActivities] = useState([{ name: '', fillIntervals: [] }]);
    const [errors, setErrors] = useState({});

    const handleIntervalChange = (idx, field, value) => {
        const copy = intervals.map((it, i) =>
            i === idx ? { ...it, [field]: value } : it
        );
        setIntervals(copy);
    };
    const addInterval = (idx = intervals.length) => {
        setIntervals([
            ...intervals.slice(0, idx + 1),
            { lower: '', upper: '' },
            ...intervals.slice(idx + 1),
        ]);
    };

    const removeInterval = (idx) => {
        if (intervals.length === 1) return;

        const newIntervals = intervals.filter((_, i) => i !== idx);
        setIntervals(newIntervals);

        const validLabels = newIntervals.map(it => `${it.lower}-${it.upper}`);

        setActivities(activities.map(activity => ({
            ...activity,
            fillIntervals: activity.fillIntervals.filter(label =>
                validLabels.includes(label)
            )
        })));
    };

    const handleActivityName = (idx, value) => {
        const copy = activities.map((a, i) =>
            i === idx ? { ...a, name: value } : a
        );
        setActivities(copy);
    };
    const handleActivitySelect = (idx, value) => {
        const copy = activities.map((a, i) =>
            i === idx ? { ...a, fillIntervals: value } : a
        );
        setActivities(copy);
    };
    const addActivity = () =>
        setActivities([...activities, { name: '', fillIntervals: [] }]);
    const removeActivity = (idx) =>
        setActivities(activities.filter((_, i) => i !== idx));
    const addActivityAt = (idx = activities.length - 1) => {
        setActivities([
            ...activities.slice(0, idx + 1),
            { name: '', fillIntervals: [] },
            ...activities.slice(idx + 1),
        ]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErr = {};

        // basic required checks
        if (!icalUrl) newErr.icalUrl = 'Required';
        else {
            try { new URL(icalUrl) }
            catch { newErr.icalUrl = 'Invalid URL' }
        }
        if (!companyName) newErr.companyName = 'Required';
        if (!userName) newErr.userName = 'Required';
        if (!job) newErr.job = 'Required';
        if (!month) newErr.month = 'Required';
        if (!year) newErr.year = 'Required';

        // intervals validation
        intervals.forEach((it, i) => {
            const low = Number(it.lower), high = Number(it.upper);
            if (it.lower === '') newErr[`int-${i}-lower`] = 'Required';
            else if (isNaN(low)) newErr[`int-${i}-lower`] = 'Must be a number';
            else if (i > 0 && low < Number(intervals[i - 1].upper))
                newErr[`int-${i}-lower`] =
                    `≥ previous upper (${intervals[i - 1].upper})`;
            if (it.upper === '') newErr[`int-${i}-upper`] = 'Required';
            else if (isNaN(high)) newErr[`int-${i}-upper`] = 'Must be a number';
            else if (high <= low)
                newErr[`int-${i}-upper`] = 'Must be > lower';
        });

        // activities validation
        activities.forEach((act, i) => {
            if (!act.name) newErr[`act-${i}-name`] = 'Required';
        });

        setErrors(newErr);
        if (Object.keys(newErr).length) return;

        // build payload
        const payload = {
            ical_url: icalUrl,
            company_name: companyName,
            name: userName,
            job,
            month: Number(month),
            year: Number(year),
            intervals: intervals.map(it => `${it.lower}-${it.upper}`),
            activities: activities.map(a => ({
                name: a.name,
                fillIntervals: a.fillIntervals,
            }))
        };

        try {
            const res = await axios.post('/api/report/monthly_report', payload, {
                responseType: 'blob'
            });
            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const userNameFormatted = userName.replace(/\s+/g, '_').toLowerCase();
            link.download = `monthly_report_${userNameFormatted}_${month}_${year}.xlsx`;
            link.click();
        } catch (err) {
            console.error(err);
            alert('Error generating report');
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Monthly Report
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* ── Section 1 ── */}
                <Typography variant="h6" gutterBottom>
                    General Info
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="iCal URL (Leaveboard -> My Account -> Integrations -> My absence iCalendar URL V2)"
                            fullWidth
                            value={icalUrl}
                            onChange={e => setIcalUrl(e.target.value)}
                            error={!!errors.icalUrl}
                            helperText={errors.icalUrl}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Company Name"
                            fullWidth
                            value={companyName}
                            onChange={e => setCompanyName(e.target.value)}
                            error={!!errors.companyName}
                            helperText={errors.companyName}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Your Name"
                            fullWidth
                            value={userName}
                            onChange={e => setUserName(e.target.value)}
                            error={!!errors.userName}
                            helperText={errors.userName}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Job"
                            fullWidth
                            value={job}
                            onChange={e => setJob(e.target.value)}
                            error={!!errors.job}
                            helperText={errors.job}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth error={!!errors.month}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                label="Month"
                                value={month}
                                onChange={e => setMonth(e.target.value)}
                            >
                                {monthOptions.map(m => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.month}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth error={!!errors.year}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                label="Year"
                                value={year}
                                onChange={e => setYear(e.target.value)}
                            >
                                {yearOptions.map(y => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.year}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* ── Section 2: Intervals ── */}
                <Box mt={4}>
                    <Typography variant="h6">Time Intervals</Typography>
                    {intervals.map((it, i) => (
                        <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 1 }}>
                            <Grid item xs={4}>
                                <TextField
                                    label="Lower (≥ prev upper)"
                                    fullWidth
                                    value={it.lower}
                                    onChange={e => handleIntervalChange(i, 'lower', e.target.value)}
                                    error={!!errors[`int-${i}-lower`]}
                                    helperText={errors[`int-${i}-lower`]}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Upper (> lower)"
                                    fullWidth
                                    value={it.upper}
                                    onChange={e => handleIntervalChange(i, 'upper', e.target.value)}
                                    error={!!errors[`int-${i}-upper`]}
                                    helperText={errors[`int-${i}-upper`]}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <IconButton onClick={() => addInterval(i)}>
                                    <AddCircleOutline />
                                </IconButton>
                                <IconButton onClick={() => removeInterval(i)}>
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                </Box>

                {/* ── Section 2: Activities ── */}
                <Box mt={4}>
                    <Typography variant="h6">Activities</Typography>
                    {activities.map((act, i) => (
                        <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 1 }}>
                            <Grid item xs={4}>
                                <TextField
                                    label="Activity Name"
                                    fullWidth
                                    value={act.name}
                                    onChange={e => handleActivityName(i, e.target.value)}
                                    error={!!errors[`act-${i}-name`]}
                                    helperText={errors[`act-${i}-name`]}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Fill Intervals (leave empty to fill one random interval)</InputLabel>
                                    <Select
                                        multiple
                                        value={act.fillIntervals}
                                        label="Fill Intervals (leave empty to fill one random interval)"
                                        onChange={e => handleActivitySelect(i, e.target.value)}
                                    >
                                        {intervals.map((it, idx) => {
                                            const label = `${it.lower}-${it.upper}`;
                                            return (
                                                <MenuItem key={idx} value={label}>
                                                    {label}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <IconButton onClick={() => addActivityAt(i)}>
                                    <AddCircleOutline />
                                </IconButton>
                                <IconButton onClick={() => removeActivity(i)}>
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutline />}
                        onClick={addActivity}
                    >
                        Add Activity
                    </Button>
                </Box>

                {/* ── Submit ── */}
                <Box mt={4} textAlign="right">
                    <Button type="submit" variant="contained" size="large">
                        Generate Report
                    </Button>
                </Box>
            </Box>
        </Container>
    );
}

export default MonthlyReport;
